import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../api/auth';
import { useLoader } from './LoaderContext';
import { AuthUser } from '../Pages/Users/types';

interface AuthContextType {
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  userData: AuthUser | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userData, setUserData] = useState<AuthUser | null>(null);
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();

  const refreshAuth = useCallback(async () => {
    showLoader();
    try {
      await auth.refresh();
      const response = await auth.getUser();
      setIsAuthenticated(response.isAuthenticated);
      setUserData(response.responseBody);
    } catch (error) {
      setIsAuthenticated(false);
      throw error;
    } finally {
      hideLoader();
    }
  }, [showLoader, hideLoader]);

  useEffect(() => {
    let mounted = true;

    const initialCheck = async () => {
      try {
        if (mounted) {
          await refreshAuth();
        }
      } catch (error) {
        if (mounted) {
          setIsAuthenticated(false);
        }
      }
    };

    initialCheck();

    return () => {
      mounted = false;
    };
  }, [refreshAuth]);

  const login = async (email: string, password: string) => {
    showLoader();
    try {
      await auth.login({ email, password });
      const response = await auth.getUser();
      if (!response.isAuthenticated) {
        throw new Error('Authentication failed');
      }
      setIsAuthenticated(response.isAuthenticated);
      setUserData(response.responseBody);

      navigate('/modules');
    } catch (error) {
      throw error;
    } finally {
      hideLoader();
    }
  };

  const logout = async () => {
    showLoader();
    try {
      await auth.logout();
      setIsAuthenticated(false);
      navigate('/signin');
    } catch (error) {
      throw error;
    } finally {
      hideLoader();
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, refreshAuth, userData }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
